// THE THREAD SURFACE — /thread/:id (DECISIONS.md 2026-08-06 "A thread is a walk
// you authored"). An authored chain through the graph, readable by anyone with
// the link.
//
// Built out of the Walk's parts — the same TrustBand, the same EdgeSentence for
// the evidence under a step — but the grammar diverged on purpose (2026-08-10):
// connectors do NOT render. Vertical order says `then`; indentation says
// `branches_to`; nothing between steps asks for a vote, because a single-author
// arrangement is not testimony (the vote affordance returns with multi-user
// editing, if that ships — ROADMAP). What IS votable here is the evidence: every
// supports/contradicts edge touching a step renders inside its card, and those
// are assertions about content, which is exactly what voting is for.
//
// NOT wrapped in ProtectedRoute, which is the whole point. The backend resolves
// an anonymous session to the 'all' group and nothing else, so a signed-out
// reader sees a published thread and gets a 404 on anything else. Write
// affordances gate on `user`; reading never does.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiFetch } from './config';
import { useAuth } from './AuthContext';
import { useLensWeights, recombine } from './lensWeights';
import Nav from './Nav';
import EdgeSentence, { relationPhrase } from './EdgeSentence';
import { TrustBand, fmtScore, relationColor } from './trustShared';
import { Explain } from './Explain';
// `threadOrder`, not `thread`: this file and that one collapse into a single
// file on a case-insensitive filesystem, which is how the traversal module got
// overwritten once already.
import { extractThread, allThreadNodes, lastRealStep, isEditableStep } from './threadOrder';
// The export is a second RENDERING of this page's reading, not a second reading
// — it consumes the step tree below rather than deriving its own. See that
// module's header for why it lives on the client and what it may import.
import { evidenceByStep, threadToMarkdown } from './threadText';
import { isQuestionNode, isUnscoredNode } from './walk';
import ObjectPicker from './ObjectPicker';
import { wvConfirm } from './Confirm';
import ThreadShare from './ThreadShare';
import ThreadPassOn from './ThreadPassOn';

// WHO SAID IT moved to Byline.js on 2026-08-14 and is now shared with
// ObjectDetail, Read and StreamRow. This one was the only correct of the four,
// so the shared component kept ITS semantics — the "unattributed" floor above
// all — rather than the other surfaces' habits. See Byline.js for why the slot
// ORDER is a constant rather than each surface's choice.
import Byline from './Byline';

// ————— one step —————
//
// ⚠ NO `snip` IN THIS FILE, deliberately (2026-08-14). A thread step used to
// truncate its content at 200 characters, which is the right call on an index
// row and the wrong one here: the thread page IS the reading surface, and a
// claim cut mid-sentence cannot be judged by the reader being asked to judge
// it. On a review of a long-form source the truncated steps are exactly the
// substantial ones. Index surfaces (StreamRow, QuickJump, ThreadShare, the
// pickers) keep their `snip` — see snip.js.

// How many evidence rows a step shows before pointing at the object page. A
// hub claim can carry hundreds; a thread is a reading, not an audit surface.
const EVIDENCE_SHOWN = 8;

function Step({
  step, at, siblings, stepKey, data, evidence, blendOf, user, onVoted,
  editing, onEdit, mainRelation, picking, onPicked, inBranch,
}) {
  const { node, index, repeat } = step;
  // An unscored type carries no truth value, so it gets NO band — and
  // specifically not the no-signal line, which would read as "nobody has voted
  // yet" when the truth is "there is nothing here to score". Declared from the
  // TYPE, never inferred from empty scores: the two states arrive here looking
  // identical, which is the whole reason this is a type test.
  //
  // Widened from `question` to the family 2026-08-08. It had to be: nesting
  // makes a THREAD a legal step and headings arrived in the same change, so the
  // question-only test would have put "nothing has been judged here yet" under
  // every chapter head in a review.
  const question = isQuestionNode(node);
  const unscored = isUnscoredNode(node);
  // A nested thread — a chapter in a longer review. Rendered as a link OUT
  // rather than expanded inline: the payload is ONE closure from one root, so
  // there is nothing here to expand it from, and a thread that contained itself
  // transitively would need its own visited set and depth cap on a public
  // unauthenticated route. Editorially it is also the right call — a reader
  // wants "Part II, a thread of nine steps →", not nine more cards spliced into
  // the chain they are already following.
  const nested = node.object_type === 'thread';
  const heading = node.object_type === 'heading';
  // This step's evidence — the supports/contradicts edges touching it, served
  // in the payload's `attachments` (never in `edges`, which the spine walk
  // owns). A repeat is a pointer, so its evidence rendered at first occurrence.
  const rows = (!repeat && evidence && evidence.get(node.id)) || [];
  const hasSignal = Object.values(node.scores || {}).some((s) => s !== 0);

  // Stacked, not a flex row. LensChips is a full-width wrapping block (see
  // trustShared.js) and putting it beside the content collapses the text to one
  // character per line — which is exactly what the first render of this page
  // did. Same vertical arrangement ClaimChip uses on the Walk.
  return (
    <div className="wv-card" style={{ padding: 'var(--wv-sp-3)', marginBottom: 'var(--wv-sp-2)' }}>
      <div>
        {/* A heading gets NO step number. It is a label OVER what follows, not a
            thing you read as step four — numbering it would make the count of
            steps disagree with the count of things a reader actually reads.
            Branch steps get none either (2026-08-10): every branch line restarts
            at 1, so five sibling claims under a heading would each read "1" —
            five aside markers carrying no information. Indentation is the
            branch's whole address. */}
        {!heading && !inBranch && (
          <div
            className="trust-muted"
            style={{
              fontSize: 'var(--wv-fs-xs)',
              fontVariantNumeric: 'tabular-nums',
              marginBottom: 'var(--wv-sp-1)',
            }}
          >
            {index}
          </div>
        )}
        <div style={{ minWidth: 0 }}>
          {heading ? (
            <div
              style={{
                fontSize: 'var(--wv-fs-lg)',
                fontWeight: 600,
                letterSpacing: '-0.01em',
              }}
            >
              {node.content}
            </div>
          ) : (
            <Link to={`/object/${node.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
              {node.content}
            </Link>
          )}

          {nested && (
            <div style={{ marginTop: 'var(--wv-sp-1)' }}>
              <Link to={`/thread/${node.id}`} className="wv-chip" style={{ textDecoration: 'none' }}>
                open this thread →
              </Link>
              <span
                className="trust-muted"
                style={{ fontSize: 'var(--wv-fs-xs)', marginLeft: 'var(--wv-sp-2)' }}
              >
                an authored chain of its own
              </span>
            </div>
          )}

          {repeat && (
            <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)', marginTop: 'var(--wv-sp-1)' }}>
              <Explain concept="thread-loop">
                ↺ this step is already step {repeat} of this thread — the thread loops back here
              </Explain>
            </div>
          )}

          {/* WHO SAID IT (2026-08-08). A heading is editorial framing by the
              thread's author, so it needs no byline; a nested thread has its
              own. Everything else says something, including "unattributed" —
              see Byline. */}
          {/* SPEAKER SLOTS ONLY on a thread step, and this is a deliberate
              difference from ObjectDetail rather than an oversight. Every claim
              in a decomposed review carries the same `written_with`, so the
              instrument line would repeat under all forty-odd steps and say
              nothing that distinguishes any of them — the noise
              lib/modelView.ts refuses for the same reason on the model surface.
              The focused object page shows it once, where it is a fact about
              the thing you are looking at. */}
          {!heading && !nested && (
            <Byline
              attribution={{
                authored_by: node.authored_by,
                asserted_by: node.asserted_by,
              }}
            />
          )}

          {/* A DUPLICATE is not a loop: the same claim hung under two headings,
              which is legal and often deliberate. Said out loud because without
              it a thread reads as holding more distinct claims than it does. */}
          {!repeat && step.duplicate && (
            <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)', marginTop: 'var(--wv-sp-1)' }}>
              ⧉ this claim also appears elsewhere in this thread
            </div>
          )}

          {/* A heading or nested thread gets NO "not scored" line at all
              (2026-08-10): a section title explaining that it is not a claim
              was noise on every part break, and the nested case already says
              "an authored chain of its own" beside its link. The line survives
              where absence could actually mislead — a question renders where a
              claim would, so its missing band needs the reason said. */}
          {unscored ? (
            !heading && !nested && (
              <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)', marginTop: 'var(--wv-sp-1)' }}>
                {question ? (
                  <Explain concept="question">not scored — a question is settled, not asserted</Explain>
                ) : (
                  <Explain concept="no-signal">not scored — this kind of object carries no truth value</Explain>
                )}
              </div>
            )
          ) : hasSignal ? (
            <div style={{ marginTop: 'var(--wv-sp-1)' }}>
              <Explain concept="trust-band">
                <TrustBand scores={node.scores} blend={blendOf(node)} />
              </Explain>
              <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)', marginLeft: 'var(--wv-sp-2)' }}>
                {fmtScore(blendOf(node))}
              </span>
            </div>
          ) : (
            <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)', marginTop: 'var(--wv-sp-1)' }}>
              <Explain concept="no-signal">nothing has been judged here yet</Explain>
            </div>
          )}

          {/* THE EVIDENCE (2026-08-10). What the graph says about this claim —
              every supports/contradicts edge touching it, visible and votable,
              because an assertion about content is exactly what voting is for
              (the connectors, by contrast, render nowhere on this page). The
              left border carries the relation's color so a contested step reads
              as contested at a glance; the far end's byline says who said it.
              Truncation is a door, not a fold: past EVIDENCE_SHOWN rows the
              object page has the rest. */}
          {rows.length > 0 && (
            <div style={{ marginTop: 'var(--wv-sp-2)' }}>
              {rows.slice(0, EVIDENCE_SHOWN).map((e) => (
                <div
                  key={e.id}
                  style={{
                    borderLeft: `2px solid ${relationColor(e.relation_name)}`,
                    paddingLeft: 'var(--wv-sp-2)',
                    marginBottom: 'var(--wv-sp-2)',
                  }}
                >
                  <EdgeSentence
                    edge={{
                      ...e,
                      source_id: e.source,
                      target_id: e.target,
                    }}
                    focusId={node.id}
                    onVoted={onVoted}
                    onRetyped={onVoted}
                    user={user}
                  />
                  <Byline
                    attribution={{
                      authored_by: e.source === node.id ? e.target_authored_by : e.source_authored_by,
                    }}
                  />
                </div>
              ))}
              {rows.length > EVIDENCE_SHOWN && (
                <Link
                  to={`/object/${node.id}`}
                  className="trust-muted"
                  style={{ fontSize: 'var(--wv-fs-xs)' }}
                >
                  +{rows.length - EVIDENCE_SHOWN} more on the object page →
                </Link>
              )}
            </div>
          )}

          {/* NO lens chips here, unlike an object page. Eight chips on every
              step turned a five-step thread into a wall of numbers and buried
              the thing a reader came for, which is the argument. The band is the
              summary and it carries the spread; the chips are one click away on
              the step's own page. Same call SpineStep makes on the Walk. */}

          {editing && (
            <StepControls
              step={step}
              at={at}
              siblings={siblings}
              stepKey={stepKey}
              onEdit={onEdit}
              mainRelation={mainRelation}
            />
          )}
          {picking && picking.stepKey === stepKey && (
            <ObjectPicker
              placeholder={`search for the step to insert ${picking.where} this one…`}
              onPick={(o) => onPicked(o)}
              onCancel={() => onPicked(null)}
              // The self-exclusion that lets `thread` stay a pickable TYPE
              // (2026-08-08). Threads are content now — a chapter is a thread
              // and belongs on its parent's spine — but a thread must not be a
              // step in itself, which was the original defect and is the whole
              // job of this prop. ⚠ Both halves are required: widening the
              // picker's type filter without this re-opens it exactly.
              excludeIds={[data.thread.id]}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// The author's edit affordances. Each maps onto ONE call, and the client passes
// the edge ids it can see rather than naming a step and letting the server work
// out which connector that is — the reading order is computed here (see
// threadOrder.js), and a second implementation of it on the server would be a
// second answer to "what comes next". `at` is this step's position in the list
// it belongs to, so the controls can name their neighbours.
//
// `remove` closes the gap. It used to delete only the connector INTO the step,
// which does not remove a step — it TRUNCATES the thread, because everything
// past the gap becomes unreachable from the root. Measured on a six-node
// thread: one click left one node.
function StepControls({ step, at, siblings, stepKey, onEdit, mainRelation }) {
  const { edge } = step;
  const next = siblings[at + 1];
  const prev = siblings[at - 1];
  const isBranch = edge && edge.relation_name !== mainRelation;
  const isRoot = !edge;

  // A REPEAT is not a position — it is the marker that the chain loops back to
  // an earlier step, and the step it names is already editable where it first
  // appeared. Offering controls here mints edges from a node to itself: caught
  // on the second drive, where `+ add a step at the end` anchored on the
  // cycle-closing repeat and appended the root to the root.
  if (!isEditableStep(step)) {
    return (
      <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)', marginTop: 'var(--wv-sp-2)' }}>
        this is where the thread loops — edit it at step {step.repeat}, or remove the connector above
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 'var(--wv-sp-1)', marginTop: 'var(--wv-sp-2)', flexWrap: 'wrap' }}>
      {isRoot && (
        <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)', alignSelf: 'center' }}>
          the thread opens here ·
        </span>
      )}
      {/* move DOWN swaps this step with the next; move UP is the same operation
          on the step above, which is why both buttons hand the server the same
          three edges and only the anchor differs. */}
      {prev && (
        <button className="wv-chip" onClick={() => onEdit('moveUp', step, at, siblings, stepKey)}>
          ↑ up
        </button>
      )}
      {next && (
        <button className="wv-chip" onClick={() => onEdit('moveDown', step, at, siblings, stepKey)}>
          ↓ down
        </button>
      )}
      <button className="wv-chip" onClick={() => onEdit('insertBefore', step, at, siblings, stepKey)}>
        insert before
      </button>
      <button className="wv-chip" onClick={() => onEdit('insertAfter', step, at, siblings, stepKey)}>
        insert after
      </button>
      <button className="wv-chip" onClick={() => onEdit('remove', step, at, siblings, stepKey)}>
        remove
      </button>
      {!isRoot && (
        <button className="wv-chip" onClick={() => onEdit('retype', step, at, siblings, stepKey)}>
          {isBranch ? 'make main line' : 'make a branch'}
        </button>
      )}
    </div>
  );
}

// ————— branches —————

// A branch is LISTED, not folded (2026-08-10; it was a collapsed "branches to:
// … (+N more)" chip before). The claims an author hung off a step are the
// content of the thread, not an aside a reader must click for — and with the
// connector rows gone, the indent and border ARE the words "branches to". If
// this ever labels itself again, the label belongs to the relation, and
// connector labels return with multi-user editing (ROADMAP), not before.
function Branch({ branch, path, ...rest }) {
  return (
    <div
      style={{
        margin: 'var(--wv-sp-1) 0 var(--wv-sp-2) var(--wv-sp-4)',
        borderLeft: '2px solid var(--wv-border)',
        paddingLeft: 'var(--wv-sp-3)',
      }}
    >
      <StepList steps={branch.steps} path={path} {...rest} inBranch />
    </div>
  );
}

// `path` disambiguates a step's identity across branches — the same object can
// legitimately appear in two places, and the picker has to open under the row
// that was clicked rather than under every copy of it.
function StepList({ steps, path = '', ...rest }) {
  // The tail is the last REAL step — see lastRealStep in threadOrder.js for why
  // a cycle-closing repeat must never be an anchor.
  const tailIndex = lastRealStep(steps);
  return (
    <>
      {steps.map((step, i) => {
        const stepKey = `${path}${i}:${step.edge?.id || 'root'}`;
        return (
          <React.Fragment key={`${step.node.id}:${step.edge?.id || 'root'}:${i}`}>
            {/* NO connector row (2026-08-10). The `then` edge used to render
                here as a votable EdgeSentence; a single-author arrangement is
                not testimony, so nothing between steps asks for a vote and the
                layout carries the order. See the module header. */}
            <Step step={step} at={i} siblings={steps} stepKey={stepKey} {...rest} />
            {(step.branches || []).map((b, bi) => (
              <Branch
                key={`${b.edge?.id || b.label}:${bi}`}
                branch={b}
                path={`${stepKey}.${bi}.`}
                {...rest}
              />
            ))}
          </React.Fragment>
        );
      })}
      {/* Append is the one gesture that needs no transaction — it is a single
          edge off the tail — so it gets its own row rather than hiding inside a
          step's controls. Top level only: a branch grows from its own last
          step's controls. */}
      {rest.editing && !path && tailIndex >= 0 && (
        <div style={{ marginTop: 'var(--wv-sp-2)', display: 'flex', gap: 'var(--wv-sp-2)', flexWrap: 'wrap' }}>
          <button
            className="wv-chip"
            onClick={() =>
              rest.onEdit(
                'insertAfter',
                steps[tailIndex],
                tailIndex,
                steps,
                `${path}${tailIndex}:${steps[tailIndex].edge?.id || 'root'}`
              )
            }
          >
            + add a step at the end
          </button>
          {/* A heading is CREATED here rather than searched for, which is why it
              is a second button instead of an option inside the picker: you are
              never looking for an existing heading, you are naming the part that
              starts here. */}
          <button
            className="wv-chip"
            onClick={() =>
              rest.onAddHeading({
                step: steps[tailIndex],
                at: tailIndex,
                steps,
                where: 'after',
              })
            }
          >
            + add a heading
          </button>
        </div>
      )}
    </>
  );
}

// ————— the list —————
//
// Small on purpose. It exists because a feature nobody can find is a feature
// nobody uses — the same failure migration 010 documents for relation names, one
// layer up. Threads are made from an object page or from the Walk; this is only
// the way back to one.
export function ThreadList() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!user) return;
    apiFetch('/api/threads')
      .then((r) => r.json())
      .then(setRows)
      .catch(() => setRows([]));
  }, [user]);

  return (
    <>
      <Nav />
      <div style={{ maxWidth: 720, margin: '0 auto', padding: 'var(--wv-sp-4)' }}>
        <h1>Your threads</h1>
        <p className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>
          <Explain concept="thread">
            An authored chain through the graph. Start one from any object page, or save a walk as
            one from an argument.
          </Explain>
        </p>
        {!user && <p className="trust-muted">Log in to see your threads.</p>}
        {user && rows === null && <p className="trust-muted">Loading…</p>}
        {user && rows?.length === 0 && (
          <p className="trust-muted">
            No threads yet. <Link to="/?view=everything&sort=new">Open an object</Link> and choose
            “start a thread here”.
          </p>
        )}
        {rows?.map((t) => (
          <div key={t.id} className="wv-card" style={{ padding: 'var(--wv-sp-3)', marginBottom: 'var(--wv-sp-2)' }}>
            <Link to={`/thread/${t.id}`} style={{ color: 'inherit' }}>
              {t.content}
            </Link>
            <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)', marginTop: 'var(--wv-sp-1)' }}>
              {t.published
                ? 'shared with everyone'
                : t.audience_count > 0
                  ? `shared with ${t.audience_count} audience${t.audience_count === 1 ? '' : 's'}`
                  : 'not shared'}
              {!t.root && ' · no starting point yet'}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ————— the page —————

export default function Thread() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { weights } = useLensWeights();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  // Which step row has the object picker open, and whether it is inserting
  // before or after it. One picker at a time, anchored to a row.
  const [picking, setPicking] = useState(null);
  // WHICH chip flashed, not whether one did. A boolean was enough while
  // `copy link` was the only one; with the export beside it both lit at once.
  const [copied, setCopied] = useState(null);
  // The export fold. Closed by default — this page is for reading, and taking
  // the text out is a deliberate second act.
  const [exporting, setExporting] = useState(false);
  // A secret-link token riding the page URL (?share=). Carried onto the API
  // read — the backend resolves it into a per-request visibility grant
  // (middleware/shareToken.ts); without it a link-holder 404s.
  const shareTokenParam = new URLSearchParams(window.location.search).get('share');

  const load = useCallback(
    async (spin = true) => {
      if (spin) setData(null);
      try {
        const res = await apiFetch(
          `/api/threads/${id}${shareTokenParam ? `?share=${encodeURIComponent(shareTokenParam)}` : ''}`
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setError(body.error || 'Thread not found');
          return;
        }
        setData(await res.json());
        setError(null);
      } catch (e) {
        setError('Could not load this thread');
      }
    },
    [id, shareTokenParam]
  );

  useEffect(() => {
    load();
  }, [load]);

  const onVoted = useCallback(() => load(false), [load]);

  const blendOf = useCallback((n) => recombine(n.scores || {}, weights), [weights]);

  const steps = useMemo(() => {
    if (!data || data.unrooted || !data.thread.root) return [];
    return extractThread(data.thread.root, data.nodes, data.edges, {
      mainRelation: data.main_relation,
      authorId: data.thread.creator,
    });
  }, [data]);

  // Evidence by step: each attachment row indexed under whichever of its
  // endpoints is on the thread (both, when an edge joins two steps — the row
  // renders under each, with "this" flipping ends, which is true and intended).
  //
  // Lives in threadText.js so the page and the export cannot disagree about
  // which rows belong to which step, or in what order they come.
  const evidenceByNode = useMemo(() => {
    if (!data) return new Map();
    return evidenceByStep(data.attachments, (data.nodes || []).map((n) => n.id));
  }, [data]);
  // THE EXPORT. Computed only while the fold is open — rendering a whole
  // document on every lens-weight change would be work for a page nobody is
  // exporting from.
  //
  // ⚠ THE CANONICAL URL, NOT `window.location.href`. A secret-link reader
  // arrives with `?share=<token>` in the address bar, and that token is a
  // capability: pasting it into a draft hands the link to everyone who reads
  // the post, permanently and unintentionally. The document carries the plain
  // address and lets its own footer say whether that address opens for anyone.
  const markdown = useMemo(() => {
    if (!exporting || !data) return '';
    return threadToMarkdown({
      thread: data.thread,
      steps,
      evidence: evidenceByNode,
      withheld: data.withheld,
      truncated: data.truncated,
      url: `${window.location.origin}/thread/${id}`,
    });
  }, [exporting, data, steps, evidenceByNode, id]);

  const isAuthor = !!user && !!data && data.thread.creator === user.id;

  const onEdit = useCallback(
    async (action, step, at, list, stepKey) => {
      const steps = list || [];
      const call = async (path, body) => {
        const res = await apiFetch(path, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          window.alert(b.error || 'That edit did not go through');
          return null;
        }
        return res.json().catch(() => ({}));
      };

      // The two INSERTs need an object before they can do anything, so they
      // open the picker and stop. onPicked below finishes them.
      if (action === 'insertBefore' || action === 'insertAfter') {
        setPicking({
          stepKey,
          where: action === 'insertBefore' ? 'before' : 'after',
          step,
          at,
          steps,
        });
        return;
      }

      setBusy(true);
      try {
        if (action === 'remove') {
          // Removing a step CLOSES THE GAP. Passing the outgoing connector is
          // what makes that possible: without it the server would have to
          // re-derive which step comes next, and the reading order lives here.
          const r = await call(`/api/threads/${id}/unstitch`, {
            edge_id: step.edge?.id || null,
            next_edge_id: steps[at + 1]?.edge?.id || null,
          });
          if (r?.orphanedBranches > 0) {
            window.alert(
              `Removed. ${r.orphanedBranches} branch${r.orphanedBranches === 1 ? '' : 'es'} hung off that step and ` +
                `${r.orphanedBranches === 1 ? 'is' : 'are'} now off the thread — the objects and edges are still in the graph.`
            );
          }
        } else if (action === 'moveDown' || action === 'moveUp') {
          // ONE operation for both buttons: moving X down is moving the step
          // above it up. The anchor differs, the edit does not.
          const i = action === 'moveDown' ? at : at - 1;
          if (!steps[i] || !steps[i + 1]) return;
          await call(`/api/threads/${id}/move`, {
            edge_id: steps[i].edge?.id || null,
            next_edge_id: steps[i + 1].edge?.id,
            after_edge_id: steps[i + 2]?.edge?.id || null,
          });
        } else if (action === 'retype') {
          if (!step.edge) return;
          const to = step.edge.relation_name === data.main_relation ? 'branches_to' : data.main_relation;
          const res = await apiFetch(`/api/relation-instances/${step.edge.id}/retype`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ relation_name: to }),
          });
          if (!res.ok) {
            const b = await res.json().catch(() => ({}));
            window.alert(b.error || 'Could not change that connector');
          }
        }
        await load(false);
      } finally {
        setBusy(false);
      }
    },
    [data, id, load]
  );

  // Finish an insert once the picker has an object.
  //
  // BEFORE and AFTER are different operations, not one with a flag. Inserting
  // before an existing step splits the connector above it, which is a splice —
  // three writes in a transaction. Inserting after the LAST step splits nothing:
  // it is a single new edge off the tail, and routing it through splice would
  // invent a connector to split.
  // The insert itself, by object id. Extracted from onPicked 2026-08-08 so the
  // "add a heading" path can reuse it: a heading is minted rather than picked,
  // but everything after "which object goes here" is identical, and a second
  // copy of the splice/append/root branching is how the two would drift.
  const insertAt = useCallback(
    async (req, objectId) => {
      const { step, at, steps, where } = req;
      const splitEdge = where === 'before' ? step.edge?.id : steps[at + 1]?.edge?.id;
      if (splitEdge) {
        const res = await apiFetch(`/api/threads/${id}/splice`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ edge_id: splitEdge, insert_id: objectId }),
        });
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          window.alert(b.error || 'Could not insert there');
        }
      } else if (where === 'after') {
        // append past the tail
        const rels = await (await apiFetch('/api/relations')).json();
        const relId = rels.find((r) => r.name === data.main_relation)?.id;
        if (!relId) return;
        const res = await apiFetch('/api/relation-instances', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            relation_id: relId,
            source_id: step.node.id,
            target_id: objectId,
            polarity: 1,
            // Without this the connector is minted and simply is not on the
            // thread, so the append reports success and the chain reads as
            // ending here (2026-08-08, explicit membership).
            thread_id: id,
          }),
        });
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          window.alert(b.error || 'Could not add that step');
        }
      } else {
        // before the ROOT: the thread now opens on the new object, joined to
        // what used to be first.
        const rels = await (await apiFetch('/api/relations')).json();
        const relId = rels.find((r) => r.name === data.main_relation)?.id;
        if (!relId) return;
        await apiFetch('/api/relation-instances', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            relation_id: relId,
            source_id: objectId,
            target_id: step.node.id,
            polarity: 1,
            thread_id: id,
          }),
        });
        await apiFetch(`/api/threads/${id}/root`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ root_id: objectId }),
        });
      }
      await load(false);
    },
    [id, data, load]
  );

  const onPicked = useCallback(
    async (picked) => {
      const req = picking;
      setPicking(null);
      if (!picked || !req) return;
      setBusy(true);
      try {
        await insertAt(req, picked.id);
      } finally {
        setBusy(false);
      }
    },
    [picking, insertAt]
  );

  // Mint a heading and put it straight into the chain. The one creation door on
  // this page, and it lives here because this is the only place headings are
  // used — a heading with no thread under it is an orphan, and the generic
  // create page would happily make one.
  //
  // `ai_visible: true` with no visibility targets is the `private_ai` shape: the
  // author and their connector can read it, nobody else, and publishing the
  // thread is what shares it along with every other member. A heading held back
  // from a published thread would leave an unlabelled gap where a section break
  // should be.
  const onAddHeading = useCallback(
    async (req) => {
      const text = window.prompt('Heading — the part this marks the start of:');
      if (!text || !text.trim()) return;
      setBusy(true);
      try {
        const res = await apiFetch('/api/objects', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content: text.trim(),
            object_type: 'heading',
            visibility_targets: [],
            model_access: 'public_ai',
          }),
        });
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          window.alert(b.error || 'Could not create that heading');
          return;
        }
        const made = await res.json();
        await insertAt(req, made.id);
      } finally {
        setBusy(false);
      }
    },
    [insertAt]
  );

  // Publishing moved into ThreadShare.js (2026-08-13) — one panel for every
  // audience: everyone, groups, secret links, unpublish. The two-call
  // audit-then-confirm shape survives there for all of them.
  const flashCopy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 2000);
    } catch {
      /* clipboard refused — see below for why that is survivable in both cases */
    }
  };
  const copyLink = () => flashCopy('link', window.location.href);

  if (error) {
    return (
      <>
        <Nav />
        <div className="wv-panel" style={{ maxWidth: 720, margin: '0 auto', padding: 'var(--wv-sp-4)' }}>
          <h1>Thread</h1>
          <p className="trust-muted">{error}</p>
          {!user && (
            <p className="trust-muted">
              This thread may not be shared publicly. <Link to="/login">Log in</Link> if you have access.
            </p>
          )}
        </div>
      </>
    );
  }

  if (!data) {
    return (
      <>
        <Nav />
        <div style={{ maxWidth: 720, margin: '0 auto', padding: 'var(--wv-sp-4)' }}>
          <p className="trust-muted">Loading…</p>
        </div>
      </>
    );
  }

  const total = allThreadNodes(steps).size;

  return (
    <>
      <Nav />
      <div style={{ maxWidth: 720, margin: '0 auto', padding: 'var(--wv-sp-4)' }}>
        <h1 style={{ marginBottom: 'var(--wv-sp-1)' }}>{data.thread.content}</h1>
        <div
          className="trust-muted"
          style={{ fontSize: 'var(--wv-fs-sm)', marginBottom: 'var(--wv-sp-3)' }}
        >
          <Explain concept="thread">
            {total} step{total === 1 ? '' : 's'}, following{' '}
            {/* main relation first — it is the line, the rest are branches off
                it, and listing them alphabetically said the opposite */}
            {[...data.thread.relations]
              .sort((a, b) => Number(b === data.main_relation) - Number(a === data.main_relation))
              .map((r) => relationPhrase(r, 1))
              .join(' and ')}
          </Explain>
          {data.thread.published || (data.thread.audience_targets || []).length > 0 ? (
            <>
              {!data.thread.published && <span> · shared with an audience</span>}
              <button className="wv-chip" style={{ marginLeft: 'var(--wv-sp-2)' }} onClick={copyLink}>
                {copied === 'link' ? 'link copied' : 'copy link'}
              </button>
            </>
          ) : (
            <span> · not shared</span>
          )}
          {/* REVIEW THE CONTENTS. Tagging a thread tags its SPINE, not the
              claims hung off it, so a tag-filtered review pass could not see a
              thread's members at all. This is the path that can — the members as
              a ranked stream, contested first. */}
          {user && total > 0 && (
            <Link
              to={`/?thread=${data.thread.id}&sort=contested`}
              className="wv-chip"
              style={{ marginLeft: 'var(--wv-sp-2)', textDecoration: 'none' }}
            >
              review these claims →
            </Link>
          )}
          {/* TAKE THE TEXT OUT. Deliberately OUTSIDE the published branch above:
              a thread that has not been shared is exactly the one whose text you
              want, because the export is how the reading leaves the app when the
              link is not going anywhere. Reading is never gated on `user` on this
              surface, and this is a read. */}
          {steps.length > 0 && (
            <button
              className="wv-chip"
              style={{ marginLeft: 'var(--wv-sp-2)' }}
              onClick={() => setExporting((e) => !e)}
            >
              {exporting ? 'hide the text' : 'copy as text'}
            </button>
          )}
        </div>

        {/* A FOLD, not a bare clipboard write. `copyLink` can swallow a
            clipboard failure in silence because the URL is in the address bar
            either way; an export has no second source, so a silent failure
            would be a lie about what you now hold. The textarea IS that
            fallback — and it lets you read what you are about to paste. A
            textarea's value is a React prop, so no HTML sink (Markdown.js). */}
        {exporting && (
          <div className="wv-panel" style={{ padding: 'var(--wv-sp-3)', marginBottom: 'var(--wv-sp-3)' }}>
            <div
              className="trust-muted"
              style={{ fontSize: 'var(--wv-fs-xs)', marginBottom: 'var(--wv-sp-2)' }}
            >
              The whole thread as markdown — every step in full, every piece of evidence, and
              no trust scores (they are weighted for <em>you</em> and mean less once they
              leave this page).
            </div>
            <textarea
              readOnly
              value={markdown}
              onFocus={(e) => e.target.select()}
              rows={16}
              style={{ width: '100%', fontFamily: 'var(--wv-mono, monospace)', fontSize: 'var(--wv-fs-xs)' }}
            />
            <button
              className="wv-chip"
              style={{ marginTop: 'var(--wv-sp-2)' }}
              onClick={() => flashCopy('text', markdown)}
            >
              {copied === 'text' ? 'copied' : 'copy all'}
            </button>
          </div>
        )}

        {/* An honest report that the reader is not seeing all of it. A truncated
            thread and a finished one look identical otherwise, and the
            difference is the whole meaning of the last step. */}
        {data.withheld > 0 && (
          <div className="wv-tint" style={{ padding: 'var(--wv-sp-2)', marginBottom: 'var(--wv-sp-3)' }}>
            <Explain concept="thread-withheld">
              This thread continues into {data.withheld} step
              {data.withheld === 1 ? '' : 's'} you cannot see. What is below is the part that is
              shared with you.
            </Explain>
          </div>
        )}
        {data.truncated && (
          <div className="wv-tint" style={{ padding: 'var(--wv-sp-2)', marginBottom: 'var(--wv-sp-3)' }}>
            This thread is longer than one sitting — it has been cut here.
          </div>
        )}
        {/* WHOSE TRUST IS THIS? (2026-08-08.) The bands below are viewer-weighted, and
            EigenTrust personalizes on the reader's own vote-activated `trusts` edges. With
            none — every signed-out reader, and every signed-in one who has not staked trust
            yet — the anchor silently falls back to a UNIFORM weight over every actor, so the
            number is a GLOBAL ranking rendered in exactly the same band as a personalized one.
            This is the ruled public surface, so it is where that gets said out loud. Nothing
            about the math changes; the flag was already computed and only the inspector saw it. */}
        {data.anchor_personalized === false && steps.length > 0 && (
          <div className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)', marginBottom: 'var(--wv-sp-3)' }}>
            These trust readings are not personalised to you — you have not staked trust in
            anyone yet, so they show the graph’s overall standing rather than what follows from
            whom <em>you</em> trust.
          </div>
        )}

        {data.unrooted || !steps.length ? (
          <p className="trust-muted">
            This thread has no steps yet.{' '}
            {isAuthor && 'Open an object and use “start a thread here”, or draw a `then` connector from its first step.'}
          </p>
        ) : (
          <StepList
            steps={steps}
            data={data}
            evidence={evidenceByNode}
            blendOf={blendOf}
            user={user}
            onVoted={onVoted}
            editing={editing && isAuthor}
            onEdit={onEdit}
            onPicked={onPicked}
            onAddHeading={onAddHeading}
            picking={picking}
            mainRelation={data.main_relation}
          />
        )}

        {isAuthor && (
          <div className="wv-panel" style={{ marginTop: 'var(--wv-sp-4)', padding: 'var(--wv-sp-3)' }}>
            <button className="wv-chip" disabled={busy} onClick={() => setEditing((e) => !e)}>
              {editing ? 'done editing' : 'edit this thread'}
            </button>{' '}
            <button
              className="wv-chip"
              disabled={busy}
              onClick={async () => {
                if (!(await wvConfirm('Delete this thread? The steps and their connectors stay in the graph.', { confirmLabel: 'Delete thread' }))) return;
                await apiFetch(`/api/threads/${id}`, { method: 'DELETE' });
                navigate('/');
              }}
            >
              delete thread
            </button>
            <ThreadShare threadId={id} published={data.thread.published} onChanged={() => load(false)} />
          </div>
        )}

        {/* The delegate's door (2026-08-16). Not in the author's panel above —
            a delegate may do exactly one of the things that panel offers, and
            the server tells us whether this reader holds the capability. */}
        {!isAuthor && data.may_share_onward && (
          <div style={{ marginTop: 'var(--wv-sp-4)' }}>
            <ThreadPassOn threadId={id} onChanged={() => load(false)} />
          </div>
        )}
      </div>
    </>
  );
}
